import { createMemo } from "solid-js"
import dijkstraDirectionCharsLowerCase from "../../lib/core/direction/dijkstraDirectionCharsLowerCase"
import { EntityTypeId } from "../../lib/core/entity/EntityTypeId.enum"
import { sample } from "../../lib/core/rng/index"
import resetEntityRngConfig from "../../lib/core/rng/resetEntityRngConfig"
import tmpRng from "../../lib/core/rng/tmpRng"
import getEntityGrassWaveRandomBatchCount from "../../lib/three/objects/DetailProps/GrassWaves/getEntityGrassWaveRandomBatchCount"
import isEntityWithGrassWaves from "../../lib/three/objects/DetailProps/GrassWaves/isEntityWithGrassWaves"
import isNumberListSame from "../signal/equals/isNumberListSame"
import createSharedCachedFactory from "./createSharedCachedFactory"
import { createSharedTileGraphNodeCollectionMemo } from "./createSharedTileGraphNodeCollectionMemo"
import { useSharedManhattanDistanceToLandListMemo } from "./useSharedManhattanDistanceToLandListMemo"

/**
 * Shared memo that calculates grass batch counts for all tiles with grass wave entities.
 * 
 * This memo provides a cached Int8Array where each index corresponds to a tile position
 * and contains the number of grass batches that should be generated for that tile.
 * The calculation is deterministic based on entity type and seeded RNG.
 * 
 * @param engine - The game engine instance
 * @returns Memo function that returns Int8Array of batch counts per tile
 */
export const useSharedGrassBatchCountListMemo = createSharedCachedFactory((engine) => {
  const getTileGraphNodesList = createSharedTileGraphNodeCollectionMemo(engine)
  const getManhattanDistanceToLandList = useSharedManhattanDistanceToLandListMemo(engine)

  const memo = createMemo<Int8Array>(() => {
    // console.time('useSharedGrassBatchCountListMemo.memo')
    const tpNodeList = getTileGraphNodesList()
    const manhattanDistanceToLandList = getManhattanDistanceToLandList()
    const len = tpNodeList.length
    const list = new Int8Array(len)
    list.fill(0)

    // Calculate batch count for each tile
    for (let index = 0; index < len; index++) {
      const node = tpNodeList[index]!
      // Find grass wave entity in this tile
      const grassEnt = node.ents().find(isEntityWithGrassWaves)
      if (grassEnt) {
        const { id: entId, etype_id } = grassEnt

        // Reset RNG to entity-specific seed for deterministic results
        resetEntityRngConfig(entId, tmpRng)
        let grassBatchesCount = getEntityGrassWaveRandomBatchCount(etype_id, tmpRng)

        if (distanceToLandBonusPoolEntityTypes.includes(etype_id)) {
          const md = manhattanDistanceToLandList[index]
          const pool = distanceToLandBonusPools[md]
          if (pool) {
            grassBatchesCount += sample(pool, tmpRng)!
          }
        }
        if (diverseNeighborBonusEntityTypes.includes(etype_id)) {
          for (const dirChar of dijkstraDirectionCharsLowerCase) {
            const neighbor = node[dirChar]
            // const nIndex = neighbor?.index
            if (neighbor) {
              const otherEntWithGrass = neighbor.ents().find(isEntityWithGrassWaves)
              if (otherEntWithGrass && otherEntWithGrass.etype_id !== etype_id) {
                grassBatchesCount += 1
              }
            }
          }
        }
        if (grassBatchesCount > 6) {
          grassBatchesCount = 6
        }
        list[index] = grassBatchesCount
      }
    }

    // console.timeEnd('useSharedGrassBatchCountListMemo.memo')
    return list
  }, new Int8Array(0), {
    equals: isNumberListSame,
  })

  // createEffect(() => {
  //   console.log('getGrassBatchCountList.effect.value', memo())
  // })

  return memo
})

const diverseNeighborBonusEntityTypes = [
  EntityTypeId.River,
  EntityTypeId.Sea,
  EntityTypeId.Shoal,
]

const distanceToLandBonusPoolEntityTypes = [
  EntityTypeId.River,
  EntityTypeId.Sea,
]

const distanceToLandBonusPools = [
  [0, 1, 1, 2, 2, 3, 4, 5],
  [0, 0, 1, 1, 2, 2, 3, 4],
  [0, 0, 0, 1, 1, 2, 2, 3],
]